import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import Cookies from 'js-cookie';

import { useCart } from 'hooks/useCart';

/**
 * posthog-js's default `localStorage+cookie` persistence puts `$sesid` (a
 * timestamp/UUID array) in a `ph_<token>_posthog` cookie sent on every
 * request. That shape trips WAF SQLi-anomaly rules (Cloudflare "Inbound
 * Anomaly Score Exceeded") on our own /api/v1/lead, /api/v1/analytics and
 * /api/v3/payment routes. Switching to `persistence: 'localStorage'` stops
 * new cookies from being set, but visitors who already have the old cookie
 * keep sending it until it expires (365 days) unless we remove it. See FIN-7948.
 */
export const clearLegacyPosthogCookie = (projectToken: string) => {
  const cookieName = `ph_${projectToken}_posthog`;
  Cookies.remove(cookieName, { path: '/' });

  // posthog-js may have set it cross-subdomain (e.g. `.mlsociety.co`,
  // `.aprovatotal.com.br`) depending on its own root-domain detection —
  // it finds the real registrable domain by test-writing a cookie at each
  // hostname suffix and letting the browser's public-suffix check reject
  // the ones that aren't, which is why a two-label ccTLD like `com.br`
  // (vs. single-label ones like `.co`) also needs a subdomain dropped, not
  // just the last two labels. Try every suffix instead of guessing one:
  // a domain that never held the cookie is a harmless no-op removal.
  const parts = window.location.hostname.split('.');
  for (let labelsToDrop = 1; labelsToDrop < parts.length; labelsToDrop += 1) {
    Cookies.remove(cookieName, {
      path: '/',
      domain: `.${parts.slice(labelsToDrop).join('.')}`,
    });
  }
};

const initPosthog = () => {
  const projectToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;

  if (!projectToken) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        'NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN required by PostHog is missing ' +
          'or un-configured. Events will be silently missed until it is set.'
      );
    }
    return;
  }

  clearLegacyPosthogCookie(projectToken);

  posthog.init(projectToken, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    defaults: '2026-05-30',
    // See clearLegacyPosthogCookie above re: why this isn't the default
    // 'localStorage+cookie' — FIN-7948.
    persistence: 'localStorage',
    session_recording: {
      maskAllInputs: true,
      maskTextClass: 'ph-mask',
    },
    loaded: (instance) => {
      if (process.env.NODE_ENV === 'development') instance.debug();
      // PostHog skips network request capture on localhost by default (to avoid
      // hot-reload noise). Force it on in dev so replay shows network calls; it
      // stays off in production.
      if (
        process.env.NODE_ENV === 'development' &&
        typeof window !== 'undefined'
      ) {
        const host = window.location?.hostname || '';
        if (host === 'localhost' || host === '127.0.0.1') {
          instance.sessionRecording!._forceAllowLocalhostNetworkCapture = true;
        }
      }
    },
  });
};

export const PosthogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  useEffect(() => {
    initPosthog();
  }, []);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};

/**
 * Links the current PostHog person to the checkout email as soon as it's
 * known, so a session/recording can be found by email in the PostHog UI.
 * Must be rendered under CartProvider, since it reads userEmail from there.
 */
export const PosthogIdentify = () => {
  const { userEmail } = useCart();
  const identifiedEmailRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userEmail || identifiedEmailRef.current === userEmail) return;

    posthog.identify(userEmail, { email: userEmail });
    identifiedEmailRef.current = userEmail;
  }, [userEmail]);

  return null;
};
